import { useState } from 'react';

interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

interface ProductFiltersProps {
  brands?: FilterOption[];
  priceRanges?: FilterOption[];
  types?: FilterOption[];
  selectedBrand?: string;
  selectedPriceRange?: string;
  selectedType?: string;
  onBrandChange?: (brand: string | undefined) => void;
  onPriceRangeChange?: (range: string | undefined) => void;
  onTypeChange?: (type: string | undefined) => void;
  onClearAll?: () => void;
  isLoading?: boolean;
}

export function ProductFilters({
  brands = [],
  priceRanges = [],
  types = [],
  selectedBrand,
  selectedPriceRange,
  selectedType,
  onBrandChange,
  onPriceRangeChange,
  onTypeChange,
  onClearAll,
  isLoading = false,
}: ProductFiltersProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasActiveFilters = selectedBrand || selectedPriceRange || selectedType;

  return (
    <div className="bg-white rounded-lg border p-4">
      {/* Mobile toggle */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="md:hidden flex items-center justify-between w-full"
      >
        <span className="font-medium">Filtros</span>
        <ChevronIcon
          className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Filters */}
      <div className={`${isExpanded ? 'block' : 'hidden'} md:block space-y-6 mt-4 md:mt-0`}>
        {/* Loading state */}
        {isLoading && (
          <div className="space-y-4">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-16 mb-2"></div>
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 rounded w-24"></div>
                <div className="h-3 bg-gray-200 rounded w-20"></div>
                <div className="h-3 bg-gray-200 rounded w-28"></div>
              </div>
            </div>
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-16 mb-2"></div>
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 rounded w-20"></div>
                <div className="h-3 bg-gray-200 rounded w-24"></div>
              </div>
            </div>
          </div>
        )}

        {/* Clear all */}
        {!isLoading && hasActiveFilters && (
          <button
            onClick={onClearAll}
            className="text-sm text-primary hover:text-primary-dark font-medium"
          >
            Limpiar filtros
          </button>
        )}

        {/* Types */}
        {!isLoading && types.length > 0 && (
          <FilterSection
            title="Tipo"
            options={types}
            selectedValue={selectedType}
            onChange={onTypeChange}
          />
        )}

        {/* Brands */}
        {!isLoading && brands.length > 0 && (
          <FilterSection
            title="Marca"
            options={brands}
            selectedValue={selectedBrand}
            onChange={onBrandChange}
          />
        )}

        {/* Price ranges */}
        {!isLoading && priceRanges.length > 0 && (
          <FilterSection
            title="Precio"
            options={priceRanges}
            selectedValue={selectedPriceRange}
            onChange={onPriceRangeChange}
          />
        )}
      </div>
    </div>
  );
}

interface FilterSectionProps {
  title: string;
  options: FilterOption[];
  selectedValue?: string;
  onChange?: (value: string | undefined) => void;
}

function FilterSection({
  title,
  options,
  selectedValue,
  onChange,
}: FilterSectionProps) {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full text-left font-medium text-gray-900"
      >
        {title}
        <ChevronIcon
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <ul className="mt-2 space-y-1">
          {options.map((option) => (
            <li key={option.value}>
              <label className="flex items-center gap-2 cursor-pointer py-1">
                <input
                  type="radio"
                  name={title}
                  checked={selectedValue === option.value}
                  onChange={() => {
                    if (selectedValue === option.value) {
                      onChange?.(undefined);
                    } else {
                      onChange?.(option.value);
                    }
                  }}
                  className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                />
                <span className="text-sm text-gray-700">{option.label}</span>
                {option.count !== undefined && (
                  <span className="text-xs text-gray-400">({option.count})</span>
                )}
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}
